/**
 * The acceptance criterion, as a component: *"UI không cho transition không hợp lệ."*
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  ONE MAP OVER THE ALLOWED SET. THE ILLEGAL MOVES HAVE NO DOM NODE.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `allowedTransitions(state)` is the whole list. There is deliberately **no** loop
 * over `INCIDENT_STATES` computing a `disabled` flag per entry, and the difference is
 * not cosmetic:
 *
 *  - A `disabled` attribute is a **rendering** decision. It is removed in devtools in
 *    two seconds, and the request it then sends is a real request. Absence is not.
 *  - A greyed control asserts *"the system will not let **you** do this"* — a
 *    statement about permission. `resolved -> acknowledged` is not a permission
 *    problem; it is a move that does not exist for anybody, ever. Rendering it greyed
 *    tells the operator to go and ask for a permission that would not help.
 *  - `closed` is terminal, so its allowed set is `[]` and a closed incident renders
 *    **no controls and one sentence**, rather than a toolbar of eight dead buttons.
 *
 * The set itself comes from `web/src/incidents/state-machine.ts`, whose test parses
 * `src/modules/incidents/state-machine.ts` and compares the two tables as sets in
 * both directions. So this component cannot offer a move the server refuses, and
 * cannot fail to offer one the server allows, without a red suite.
 *
 * ── The one control here that IS disabled, and why that one is honest ────────
 *
 * The **submit** inside an open move, while its mandatory `note` is empty (and its
 * `resolution`, when the target concludes). That greying says "you have not finished
 * saying what you are doing", which is a true sentence about the operator rather than
 * about the system's permission — the same distinction `RevokeConfirm` draws on
 * `/devices`, where the *button* is absent until two independent inputs are satisfied
 * and the reason is that a greyed control would have made the wrong claim.
 *
 * Both fields are demanded **before** sending rather than learned from a `400`:
 * `IncidentTransitionRequest.note` is `minLength: 3`, and `resolution` is required
 * when the target is one of the three concluding states — at the API and again at
 * `wp_incidents_resolve_ck`, which is the control that still holds when this file is
 * gone. Learning a mandatory field from a refusal is learning it from the wrong end.
 *
 * ── A reopen says what it costs ─────────────────────────────────────────────
 *
 * `resolved` and `false_positive` reopen into `investigating` and nowhere else. The
 * control carries two sentences, because both halves are counter-intuitive and
 * neither is visible in the button's own word: the reopen **clears the conclusion**
 * (which survives in the transition row) and **keeps the original deadline and any
 * breach**. An owner who could resolve-and-reopen for a fresh SLA would have a button
 * that turns a missed deadline into a met one.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { IncidentTransitionRequest } from '../../api/types';
import type { WorkforceApiError } from '../../api/errors';
import { allowedTransitionsFromRefusal } from '../../api/useApiMutation';
import {
  allowedTransitions,
  isConcludingState,
  isReopen,
  isTerminal,
  type IncidentState,
} from '../../incidents/state-machine';
import { STATE_LABEL, TRANSITION_LABEL } from '../../incidents/labels';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const NOTE_MIN = 3;
const NOTE_MAX = 2000;
const RESOLUTION_MAX = 2000;

export interface TransitionControlsProps {
  readonly state: IncidentState;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onSubmit: (body: IncidentTransitionRequest) => void;
}

export function TransitionControls({
  state,
  pending,
  error,
  onSubmit,
}: TransitionControlsProps): ReactNode {
  const { t } = useI18n();
  const [target, setTarget] = useState<IncidentState | null>(null);
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState('');

  const moves = allowedTransitions(state);
  const concluding = target !== null && isConcludingState(target);
  const ready =
    target !== null &&
    note.trim().length >= NOTE_MIN &&
    (!concluding || resolution.trim().length > 0);

  // The server's own answer to "what could you have done instead". Printed rather
  // than re-derived from this client's table: if the two ever disagree the one that
  // decides is the server's, and printing our own list would hide the disagreement
  // at the exact moment it mattered.
  const serverAllowed = error === null ? [] : allowedTransitionsFromRefusal(error);

  return (
    <section
      className="wp-section"
      aria-labelledby="l5-transition-heading"
      data-testid="l5-transitions"
    >
      <h3 className="wp-section__title" id="l5-transition-heading">
        {t('incidents.transition.title')}
      </h3>

      {isTerminal(state) ? (
        // No controls at all — not eight disabled ones. `TRANSITIONS.closed` is `[]`,
        // which is a statement rather than an omission, and the sentence says which.
        <StatusNote
          severity="info"
          title={t('incidents.transition.terminal.title')}
          testId="l5-transition-terminal"
        >
          <p>{t('incidents.transition.terminal.body')}</p>
        </StatusNote>
      ) : (
        <>
          {/* The moves the machine allows, as full 44px actions and not as a
              segmented filter: each one is a write that changes the record, and a
              34px chip is the affordance of a view control (DESIGN.md §8, Luật 44).
              They wrap rather than scroll — there are at most four. */}
          <div className="wp-c-actions" data-testid="l5-transition-buttons">
            {moves.map((move) => (
              <button
                key={move}
                className="wp-button"
                type="button"
                aria-pressed={target === move}
                data-testid={`l5-move-${move}`}
                onClick={() => {
                  setTarget(move);
                  setResolution('');
                }}
              >
                {t(TRANSITION_LABEL[move])}
              </button>
            ))}
          </div>
          <p className="wp-small wp-muted" data-testid="l5-transition-note">
            {t('incidents.transition.derived', {
              state: t(STATE_LABEL[state]),
              count: moves.length,
            })}
          </p>
        </>
      )}

      {target === null ? null : (
        <div className="wp-panel" data-testid="l5-transition-form">
          <h4 className="wp-panel__title">
            {t('incidents.transition.form', { to: t(STATE_LABEL[target]) })}
          </h4>

          {isReopen(state, target) ? (
            <StatusNote
              severity="medium"
              title={t('incidents.reopen.title')}
              testId="l5-reopen-warning"
            >
              <p data-testid="l5-reopen-clears">{t('incidents.reopen.clears')}</p>
              <p data-testid="l5-reopen-sla">{t('incidents.reopen.sla')}</p>
            </StatusNote>
          ) : null}

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="l5-transition-note-input">
              {t('incidents.transition.noteLabel')}
            </label>
            <input
              className="wp-field__input"
              id="l5-transition-note-input"
              data-testid="l5-transition-note-input"
              type="text"
              value={note}
              maxLength={NOTE_MAX}
              autoComplete="off"
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
            <p className="wp-small wp-muted">
              {t('incidents.transition.noteRule', { min: NOTE_MIN })}
            </p>
          </div>

          {concluding ? (
            <div className="wp-field">
              <label className="wp-field__label" htmlFor="l5-transition-resolution">
                {t('incidents.transition.resolutionLabel')}
              </label>
              <input
                className="wp-field__input"
                id="l5-transition-resolution"
                data-testid="l5-transition-resolution"
                type="text"
                value={resolution}
                maxLength={RESOLUTION_MAX}
                autoComplete="off"
                onChange={(event) => {
                  setResolution(event.target.value);
                }}
              />
              <p className="wp-small wp-muted" data-testid="l5-resolution-rule">
                {t('incidents.transition.resolutionRule')}
              </p>
            </div>
          ) : null}

          <div className="wp-note__actions">
            <button
              className="wp-button wp-button--primary"
              type="button"
              data-testid="l5-transition-submit"
              disabled={!ready || pending}
              onClick={() => {
                onSubmit({
                  to_state: target,
                  note: note.trim(),
                  ...(concluding ? { resolution: resolution.trim() } : {}),
                });
              }}
            >
              {pending ? t('incidents.transition.submitting') : t('incidents.transition.submit')}
            </button>
            <button
              className="wp-button wp-button--quiet"
              type="button"
              data-testid="l5-transition-cancel"
              onClick={() => {
                setTarget(null);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* A move nobody can reach from this screen can still be issued from a stale
          one — the incident moved underneath the operator between the read and the
          press. That refusal is rendered, never swallowed: the server's own sentence
          through `ApiErrorNote`, plus the targets the server itself named. */}
      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('incidents.subjectWord')}
            testId="l5-transition-error"
          />
          {serverAllowed.length === 0 ? null : (
            <p className="wp-small" data-testid="l5-transition-server-allowed">
              {t('incidents.transition.serverAllowed', { list: serverAllowed.join(', ') })}
            </p>
          )}
        </>
      )}
    </section>
  );
}
